import React, { useState, useEffect, useRef } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { postData, ImageFileUpload } from "../utility/Utility";
import CustomLoader from "../Components/CustomLoader/CustomLoader";
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

function GreetingCardModal({ isOpen, onClose, selectedDate, festival, onGreetingCardCreated, editMode, cardData, onCardUpdated }) {
  const [loading, setLoading] = useState(false);


  console.log(cardData);
  const [formData, setFormData] = useState({
    title: "",
    message: "",
    isActive: true,
    sendOption: "email",
    audience: "both",
    imageUrl: "",
    scheduledDate: ""
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 4/3 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageValidationError, setImageValidationError] = useState("");
  const imgRef = useRef(null);

  // Populate form data when editing
  useEffect(() => {
    if (isOpen && editMode && cardData) {
      setFormData({
        title: cardData.title || "",
        message: cardData.message || "",
        isActive: cardData.isActive !== undefined ? cardData.isActive : true,
        sendOption: cardData.sendOption || "email",
        audience: cardData.audience || "both",
        imageUrl: cardData.imageUrl || "",
        scheduledDate: cardData.scheduledDate || ""
      });
      // Note: For editing, we don't pre-select image file, but show existing image
    } else if (isOpen && !editMode) {
      // Auto-fill title with festival name when creating new
      if (festival && festival.name) {
        setFormData(prev => ({
          ...prev,
          title: festival.name
        }));
      }
    }
  }, [isOpen, editMode, cardData, festival]);

  // Clean up preview URL when modal closes
  useEffect(() => {
    if (!isOpen && imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  }, [isOpen, imagePreview]);

  // Helper function to format date to YYYY-MM-DD
  const formatDateToString = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const editorConfiguration = {
    toolbar: {
      items: [
        'heading', '|',
        'bold', 'italic', 'underline', 'strikethrough', '|',
        'fontColor', 'fontBackgroundColor', '|',
        'bulletedList', 'numberedList', '|',
        'alignment', '|',
        'link', 'insertTable', 'blockQuote', '|',
        'undo', 'redo'
      ]
    },
    heading: {
      options: [
        { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
        { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
        { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
        { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' }
      ]
    },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://'
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
    },
    placeholder: 'Write your greeting card message here...',
    removePlugins: ['Markdown']
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle CKEditor changes
  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setFormData(prev => ({
      ...prev,
      message: data
    }));
  };

  // Image validation constants
  const IMAGE_VALIDATION = {
    supportedTypes: ['image/jpeg', 'image/jpg'], // Only JPG/JPEG allowed
    maxSize: 5 * 1024 * 1024, // 5MB
    allowedDimensions: [
      { width: 1080, height: 1920, ratio: 9/16, name: '9:16 (Portrait)' }, // 9:16 aspect ratio
      { width: 1080, height: 1350, ratio: 4/5, name: '4:5 (Portrait)' }   // 4:5 aspect ratio
    ],
    maxDeviation: 0 // No deviation allowed from required dimensions
  };

  // Validate image file
  const validateImageFile = (file) => {
    setImageValidationError("");

    // Check file type
    if (!IMAGE_VALIDATION.supportedTypes.includes(file.type)) {
      setImageValidationError("Please upload only JPG, PNG, or GIF images");
      return false;
    }

    // Check file size
    if (file.size > IMAGE_VALIDATION.maxSize) {
      setImageValidationError("File size should be less than 5MB");
      return false;
    }

    return true;
  };

  // Validate image dimensions
  const validateImageDimensions = (img) => {
    try {
      if (!img || typeof img !== 'object' || !img.width || !img.height) {
        setImageValidationError('Invalid image data. Please try again.');
        return false;
      }

      const { allowedDimensions } = IMAGE_VALIDATION;
      
      // Check if image matches any of the allowed dimensions exactly
      const matchedDimension = allowedDimensions.find(dim => 
        img.width === dim.width && img.height === dim.height
      );

      if (matchedDimension) {
        return true; // Exact match found
      }

      // Show specific error message for the allowed dimensions
      const dimensionList = allowedDimensions
        .map(d => `${d.width} × ${d.height} (${d.name})`)
        .join(' or ');
      
      setImageValidationError(`Unsupported image size. Please use exactly: ${dimensionList}. Your image: ${img.width} × ${img.height}px`);
      return false;
    } catch (error) {
      console.error('Error validating image dimensions:', error);
      setImageValidationError('An error occurred while validating the image. Please try again.');
      return false;
    }
  };

  // Handle file selection for image upload with validation
  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!validateImageFile(file)) {
      e.target.value = '';
      return;
    }

    // Validate image dimensions
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      if (!validateImageDimensions(img)) {
        e.target.value = '';
        return;
      }

      // If all validations pass, set the file and create preview
      setSelectedFile(file);
      setFormData(prev => ({
        ...prev,
        imageUrl: file.name
      }));

      // Create preview URL and skip crop step
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      // Don't show crop modal, accept the image as is
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setImageValidationError("Error reading image file. Please try a different image.");
      e.target.value = '';
    };

    img.src = objectUrl;
  };

  // Handle crop completion - Allow height adjustment while maintaining 9:16 aspect ratio
  const onCropComplete = (crop) => {
    const TARGET_ASPECT = 9/16;
    let newCrop = { ...crop };
    
    // If width is being changed, adjust height to maintain aspect ratio
    if (crop.width !== completedCrop?.width) {
      newCrop.height = crop.width / TARGET_ASPECT;
    } 
    // If height is being changed, adjust width to maintain aspect ratio
    else if (crop.height !== completedCrop?.height) {
      newCrop.width = crop.height * TARGET_ASPECT;
    }
    
    // Ensure minimum dimensions
    if (newCrop.width < 100) {
      newCrop.width = 100;
      newCrop.height = newCrop.width / TARGET_ASPECT;
    }
    if (newCrop.height < 100) {
      newCrop.height = 100;
      newCrop.width = newCrop.height * TARGET_ASPECT;
    }
    
    setCompletedCrop(newCrop);
    return newCrop; // Return the adjusted crop
  };

  // Handle crop change and maintain aspect ratio
  const onCropChange = (crop) => {
    const TARGET_ASPECT = 9/16;
    let newCrop = { ...crop };
    
    // If this is a resize operation, maintain aspect ratio
    if (crop.width && crop.height) {
      // If width is being changed, adjust height to maintain aspect ratio
      if (crop.width !== completedCrop?.width) {
        newCrop.height = crop.width / TARGET_ASPECT;
      } 
      // If height is being changed, adjust width to maintain aspect ratio
      else if (crop.height !== completedCrop?.height) {
        newCrop.width = crop.height * TARGET_ASPECT;
      }
    }
    
    setCrop(newCrop);
  };

  // Get cropped image
  const getCroppedImg = (image, crop, fileName) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('Canvas is empty');
            return;
          }
          blob.name = fileName;
          resolve(blob);
        },
        'image/jpeg',
        0.95
      );
    });
  };

  // Handle crop save
  const handleCropSave = async () => {
    if (!completedCrop || !imgRef.current) return;

    try {
      const croppedImageBlob = await getCroppedImg(
        imgRef.current,
        completedCrop,
        selectedFile.name
      );

      // Create a new file from the cropped blob
      const croppedFile = new File([croppedImageBlob], selectedFile.name, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });

      setSelectedFile(croppedFile);
      setShowCropModal(false);

      // Update preview
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      const previewUrl = URL.createObjectURL(croppedImageBlob);
      setImagePreview(previewUrl);

    } catch (error) {
      console.error('Error cropping image:', error);
      toast.error('Error cropping image');
    }
  };

  // Upload image if file is selected
  const uploadImage = async () => {
    if (!selectedFile) return formData.imageUrl;

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', selectedFile);

      const response = await ImageFileUpload("/api/admin/upload?is_upload=s3", uploadFormData);
      const data = await response;
      if (response?.success) {
        return data.url || data.imageUrl || data.filePath; // Adjust based on your API response
      } else {
        toast.error(data.message || "Background image upload failed");
        return null;
      }
    } catch (error) {
      toast.error("Error uploading image");
      console.error("Upload error:", error);
      return formData.imageUrl;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.message) {
      toast.error("Title and message are required");
      return;
    }

    setLoading(true);

    try {
      // Upload image first if a file is selected
      const imageUrl = await uploadImage();

      const apiData = {
        title: formData.title,
        message: formData.message,
        imageUrl: imageUrl,
        sendOption: formData.sendOption,
        audience: formData.audience,
        scheduledDate: editMode && cardData ? cardData.scheduledDate : formatDateToString(selectedDate)
      };

      let response;
      if (editMode && cardData && cardData._id) {
        // Update existing card
        response = await postData("api/greeting-cards/update", { ...apiData, cardId: cardData._id });
      } else {
        // Create new card
        response = await postData("api/greeting-cards/create", apiData);
      }

      if (response?.success) {
        toast.success(editMode ? "Greeting card updated successfully!" : "Greeting card created successfully!");
        onGreetingCardCreated && onGreetingCardCreated(response.data);
        onCardUpdated && onCardUpdated(response.data);
        handleReset();
        onClose();
      } else {
        toast.error(response?.message || `Failed to ${editMode ? 'update' : 'create'} greeting card`);
      }
    } catch (error) {
      toast.error(`Error ${editMode ? 'updating' : 'creating'} greeting card`);
      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle reset
  const handleReset = () => {
    setFormData({
      title: "",
      message: "",
      imageUrl: "",
      sendOption: "email",
      audience: "both",
      isActive: true
    });
    setSelectedFile(null);
    setCrop({ unit: '%', width: 80, aspect: 4/3 });
    setCompletedCrop(null);
    setShowCropModal(false);
    setImageValidationError("");
    // Clean up preview URL
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <CustomLoader loading={loading} />
      <ToastContainer />

      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {editMode ? "Edit Greeting Card" : "Create Greeting Card"} for {selectedDate ? selectedDate.toLocaleDateString() : 'Selected Date'}
            </h5>
            {editMode && cardData && cardData.updatedAt && (
              <div className="mt-2">
                <small className="text-muted">
                  Last Updated: {new Date(cardData.updatedAt).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </small>
              </div>
            )}
            <button type="button" className="btn-close" onClick={onClose}></button>

          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                {/* Title */}
                <div className="col-md-6">
                  <label htmlFor="title" className="form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Enter greeting card title"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Send Option */}
                <div className="col-md-6">
                  <label htmlFor="sendOption" className="form-label">
                    Send Option
                  </label>
                  <select
                    className="form-select"
                    id="sendOption"
                    name="sendOption"
                    value={formData.sendOption}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="email">Email</option>
                    <option value="whatsapp">Whatsapp</option>
                    <option value="both">Both</option>
                  </select>
                </div>

                {/* Image Upload */}
                {formData.sendOption !== "email" &&   <div className="col-12">
                  <label className="form-label">Image</label>
                  <div className="d-flex align-items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="form-control"
                      disabled={loading}
                    />
                    {selectedFile && (
                      <div className="d-flex align-items-center gap-2">
                        <i className="mdi mdi-image text-success"></i>
                        <small className="text-muted">
                          {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </small>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            setSelectedFile(null);
                            setFormData(prev => ({ ...prev, imageUrl: "" }));
                            // Clean up preview URL
                            if (imagePreview) {
                              URL.revokeObjectURL(imagePreview);
                            }
                            setImagePreview(null);
                          }}
                          disabled={loading}
                        >
                          <i className="mdi mdi-delete"></i>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="requirements-box p-3 bg-light rounded mt-2 border border-primary">
                    <h6 className="fw-bold mb-2 text-primary">🖼️ Image Requirements:</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-1">✅ <strong>Supported Resolutions (exact dimensions required):</strong></li>
                      <li className="mb-1 ps-3">• <span className="fw-bold">1080 × 1920 pixels</span> (9:16 Portrait)</li>
                      <li className="mb-1 ps-3">• <span className="fw-bold">1080 × 1350 pixels</span> (4:5 Portrait)</li>
                      <li className="mb-1">✅ <strong>Format:</strong> <span className="fw-bold">JPG/JPEG only</span></li>
                      <li className="mb-1">✅ <strong>Max File Size:</strong> <span className="fw-bold">5 MB</span></li>
                    </ul>
                    <div className="alert alert-warning mt-2 p-2 small">
                      <i className="mdi mdi-alert-circle-outline me-1"></i>
                      <strong>Note:</strong> Only the exact dimensions listed above will be accepted. No cropping will be done.
                    </div>
                  </div>
                  {imageValidationError && (
                    <div className="alert alert-danger mt-2 py-2" role="alert">
                      <small>{imageValidationError}</small>
                    </div>
                  )}
                  {/* Image Preview */}
                  {imagePreview || formData.imageUrl ? (
                    <div className="mt-3">
                      <label className="form-label">Image Preview</label>
                      <div className="border rounded p-2 bg-light">
                        <img
                          src={imagePreview || formData.imageUrl}
                          alt="Preview"
                          className="img-fluid rounded"
                          style={{ maxHeight: '200px', maxWidth: '100%' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div style={{ display: 'none', width: '100%', height: '200px', backgroundColor: '#f8f9fa', borderRadius: '4px', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                          <i className="mdi mdi-image-off text-muted" style={{ fontSize: '48px' }}></i>
                          <small className="text-muted mt-2">No Image Available</small>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3">
                      <label className="form-label">Image Preview</label>
                      <div className="border rounded p-2 bg-light d-flex align-items-center justify-content-center" style={{ height: '200px' }}>
                        <div className="text-center">
                          <i className="mdi mdi-image-plus text-muted" style={{ fontSize: '48px' }}></i>
                          <small className="text-muted d-block mt-2">No Image Selected</small>
                          <small className="text-muted">Upload an image to see preview</small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>}
                {/* Message with CKEditor */}
                <div className="col-12">
                  <label htmlFor="message" className="form-label">
                    Message <span className="text-danger">*</span>
                  </label>
                  <CKEditor
                    editor={ClassicEditor}
                    config={editorConfiguration}
                    data={formData.message}
                    onChange={handleEditorChange}
                    disabled={loading}
                  />
                </div>


              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Close
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : (editMode ? "Update Greeting Card" : "Create Greeting Card")}
            </button>
          </div>
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crop Image</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowCropModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="text-center mb-3">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <span className="fw-bold">Crop Size: </span>
                        {completedCrop ? `${Math.round(completedCrop.width)} × ${Math.round(completedCrop.height)}` : 'Adjust the crop area'}
                      </div>
                      <div className="text-muted small">
                        <i className="mdi mdi-arrow-expand-vertical me-1"></i>
                        Drag edges to adjust size
                      </div>
                    </div>
                    <ReactCrop
                      crop={crop}
                      onChange={onCropChange}
                      onComplete={onCropComplete}
                      aspect={9/16}
                      minWidth={100}
                      minHeight={100}
                      ruleOfThirds
                      style={{
                        background: '#f8f9fa',
                        padding: '10px',
                        borderRadius: '4px',
                        maxHeight: '70vh',
                        maxWidth: '100%'
                      }}
                      className="mx-auto d-block"
                    >
                    <img
                      ref={imgRef}
                      src={imagePreview}
                      alt="Crop preview"
                      style={{ maxWidth: '100%', maxHeight: '400px' }}
                    />
                    </ReactCrop>
                    <div className="mt-2 text-center text-muted small">
                      <i className="mdi mdi-information-outline me-1"></i>
                      Crop area will maintain 9:16 aspect ratio automatically
                    </div>
                  </div>
                </div>
                <div className="text-muted small">
                  <p>Use the crop tool to select the desired portion of your image.</p>
                  <p className="fw-bold text-primary">Aspect ratio is locked at 9:16 (Portrait)</p>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCropModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCropSave}
                  disabled={!completedCrop}
                >
                  Save Crop
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GreetingCardModal;
